import { useEffect, useState, useRef } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import { storeRequester } from "App";
import InputText from "components/inputs/InputText";
import CheckCircle from "components/inputs/checkBox/CheckCircle";
import ButtonEclipse from "components/buttons/ButtonEclipse";
import ModalBase from "modals/base/ModalBase";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import style from "./ShippingInfoAddModal.module.css";
import { validateInputs } from "shared/utils/Utils";
import SuperExpressive from "super-expressive";
import CustomButton from "components/buttons/CustomButton";
import P from "components/P";
import Inline from "layouts/container/Inline";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import { toast } from "react-toastify";

const ShippingInfoAddKrModal = NiceModal.create((props) => {
  const { t } = useTranslation();
  const {
    data,
    mode = 'add',
    setAdd,
    callback
  } = props;

  const [withHeader, withFooter] = [true, false];
  const [width, height] = ["min(100%, 500px)", "initial"];
  const withCloseButton = true;
  const clickOutsideToClose = false;
  const title = mode === 'add' ? t("addShippingInfo") : t("editShippingInfo");
  const buttonText = t("close");

  const modal = useRef();
  const inputs = useRef([]);

  const textFormat = {
    exp: SuperExpressive().startOfInput.caseInsensitive.oneOrMore.anyChar.endOfInput.toRegex(),
    description: "text format",
    sample: "빈칸 없어야 함",
  };

  // 폼 데이터 상태 관리
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    zonecode: "",
    province: "",
    firstName: "",
    lastName: "",
    addressDetail: "",
    phone: "",
    isDefault: mode === 'edit' ? data?.metadata?.default || false : false
  });

  // 초기 데이터 설정 (수정 모드)
  useEffect(() => {

    if (mode === 'edit' && data) {
      const initialFormData = {
        name: data.name || "",
        address: data.address_1 || "",
        zonecode: data.postal_code || "",
        province: data.province || "",
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        addressDetail: data.address_2 || "",
        phone: data.phone?.replace(/\D/g, "") || "",
        isDefault: data.metadata?.default === true
      };

      setFormData(initialFormData);

      // input refs 설정
      inputs.current[0]?.setValue(initialFormData.name);
      inputs.current[1]?.setValue(initialFormData.firstName);
      inputs.current[2]?.setValue(initialFormData.address);
      inputs.current[3]?.setValue(initialFormData.zonecode);
      inputs.current[4]?.setValue(initialFormData.addressDetail);
      inputs.current[8]?.setValue(initialFormData.isDefault);
      inputs.current[9]?.setValue(initialFormData.phone);
    }

    return () => {
      if (props.onClose) {
        props.onClose();
      }
    };
  }, [data, mode]);

  const addressKrModalClick = () => {
    NiceModal.show("addressKr", { onCallback: addressKrCallback });
  };

  const addressKrCallback = (dataFromModal) => {
    if (!dataFromModal) return;

    setFormData(prev => ({
      ...prev,
      address: dataFromModal.address,
      zonecode: dataFromModal.zonecode
    }));

    // input refs 업데이트
    inputs.current[2]?.setValue(dataFromModal.address);
    inputs.current[3]?.setValue(dataFromModal.zonecode);
  };

  const handleSubmit = async () => {
    const requiredFields = [0, 1, 2, 3, 4, 9];
    const isEmpty = requiredFields.some((index) => {
      const value = inputs.current && inputs.current[index]?.getValue().toString().trim();
      return !value;
    });

    if (isEmpty) {
      toast.error(t("배송지 정보를 모두 입력해주세요"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    const isDefault = inputs.current[8]?.getValue();


    const addressData = {
      address: {
        name: inputs.current[0]?.getValue().toString(),
        first_name: inputs.current[1]?.getValue().toString(),
        last_name: "",
        province: "",
        postal_code: inputs.current[3]?.getValue().toString(),
        address_1: inputs.current[2]?.getValue().toString(),
        address_2: inputs.current[4]?.getValue().toString(),
        city: "city",
        country_code: "kr",
        phone: inputs.current[9]?.getValue().toString(),
        company: "default",
        // 체크박스 상태에 따라 metadata 설정
        metadata: { default: isDefault }
      },
    };


    try {
      let result;
      if (mode === 'add') {
        result = await storeRequester.addShippingAddress(addressData);
      } else {
        result = await storeRequester.updateShippingAddress(data.id, addressData.address);
      }

      // 먼저 모달을 닫고
      modal.current.close();

      if (callback) {
        callback(result);
      }
      setAdd?.(prev => !prev);

    } catch (error) {
      toast.error(t("정보를 다시 확인해주세요"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
    }
  };

  const addressBtnStyle = {
    backgroundColor: "#4d4f56",
    color: "white",
    padding: "5px 15px",
  };

  return (
    <ModalBase
      slideUp
      ref={modal}
      width={width}
      height={height}
      withHeader={withHeader}
      withFooter={withFooter}
      withCloseButton={withCloseButton}
      clickOutsideToClose={clickOutsideToClose}
      title={title}
      buttonText={buttonText}
      overflow={"visible"}
    >
      <div style={{ padding: "15px" }}>
        <VerticalFlex padding={10} gap={10} flexStart>
          <FlexChild>
            <Inline>
              <P weight={"bold"}>배송지 명 </P>
              <P weight={"bold"} color={"var(--main-color)"}>*</P>
            </Inline>
          </FlexChild>
          <FlexChild>
            <InputText
              ref={(el) => (inputs.current[0] = el)}
              placeHolderMoveUp={true}
              placeHolder={t("예) 집, 회사(최대 10자)")}
              regExp={[textFormat]}
              defaultValue={formData.name}
            />
          </FlexChild>

          <FlexChild>
            <Inline>
              <P weight={"bold"}>받으실 분 </P>
              <P weight={"bold"} color={"var(--main-color)"}>*</P>
            </Inline>
          </FlexChild>
          <FlexChild>
            <InputText
              ref={(el) => (inputs.current[1] = el)}
              placeHolderMoveUp={true}
              placeHolder={t("받으실 분의 이름을 작성하세요")}
              regExp={[textFormat]}
              defaultValue={formData.firstName}
            />
          </FlexChild>

          <FlexChild>
            <Inline>
              <P weight={"bold"}>주소 </P>
              <P weight={"bold"} color={"var(--main-color)"}>*</P>
            </Inline>
          </FlexChild>
          <FlexChild>
            <InputText
              value={formData.address}
              disabled
              ref={(el) => (inputs.current[2] = el)}
              placeHolderMoveUp={true}
              placeHolder={t("주소를 검색해주세요.")}
              regExp={[textFormat]}
            />
          </FlexChild>

          <FlexChild>
            <HorizontalFlex gap={20}>
              <FlexChild>
                <InputText
                  value={formData.zonecode}
                  disabled
                  ref={(el) => (inputs.current[3] = el)}
                  placeHolderMoveUp={true}
                  placeHolder={t("우편번호")}
                  regExp={[textFormat]}
                />
              </FlexChild>
              <FlexChild width={"max-content"}>
                <CustomButton
                  onClick={addressKrModalClick}
                  text={"주소 검색"}
                  style={addressBtnStyle}
                />
              </FlexChild>
            </HorizontalFlex>
          </FlexChild>

          <FlexChild>
            <InputText
              ref={(el) => (inputs.current[4] = el)}
              placeHolderMoveUp={true}
              placeHolder={t("상세주소를 입력해주세요")}
              regExp={[textFormat]}
              defaultValue={formData.addressDetail}
            />
          </FlexChild>

          <FlexChild>
            <div className={style.pure}>
              <InputText
                value={"d221df3e-5f43-11ed-af22-00163e916a3b"}
                ref={(el) => (inputs.current[5] = el)}
                hidden
                regExp={[textFormat]}
              />
              <InputText
                value={"d9f19a46-5f43-11ed-af22-00163e916a3b"}
                ref={(el) => (inputs.current[6] = el)}
                hidden
                regExp={[textFormat]}
              />
              <InputText
                value={"21194cfb-5f51-11ed-af22-00163e916a3b"}
                ref={(el) => (inputs.current[7] = el)}
                hidden
                regExp={[textFormat]}
              />
            </div>
          </FlexChild>

          <FlexChild>
            <CheckCircle
              ref={(el) => (inputs.current[8] = el)}
              label={t("saveAsDefaultShippingInfo")}
              defaultChecked={formData.isDefault}
            />
          </FlexChild>

          <FlexChild>
            <Inline>
              <P weight={"bold"}>휴대폰 번호 </P>
              <P weight={"bold"} color={"var(--main-color)"}>*</P>
            </Inline>
          </FlexChild>
          <FlexChild>
            <InputText
              ref={(el) => (inputs.current[9] = el)}
              placeHolderMoveUp={true}
              placeHolder={t("- 빼고 입력해주세요")}
              regExp={[textFormat]}
              defaultValue={formData.phone}
            />
          </FlexChild>

          <FlexChild>
            <ButtonEclipse
              text={mode === 'add' ? t("등록 완료") : t("수정 완료")}
              lineHeight={"47px"}
              height={50}
              backgroundColor={"#4d4f56"}
              onClick={handleSubmit}
              borderRadius={"5px"}
            />
          </FlexChild>
        </VerticalFlex>
      </div>
    </ModalBase>
  );
});

export default ShippingInfoAddKrModal;